import { useState, useEffect } from 'react';
import api from '../api/client';

// Cache configuration
const CACHE_KEY = 'fundamental_signals_cache';
const CACHE_DURATION_HOURS = 20;

// Check if cached data is still valid
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { timestamp, data } = JSON.parse(cached);
    const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);

    if (ageHours < CACHE_DURATION_HOURS) {
      console.log(`Using cached recommendations (${ageHours.toFixed(1)}h old)`);
      return data;
    }

    console.log(`Cache expired (${ageHours.toFixed(1)}h old), fetching fresh data`);
    return null;
  } catch (err) {
    console.log('Cache read error, fetching fresh data');
    return null;
  }
};

// Save data to cache
const setCachedData = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      data
    }));
    console.log(`Cached ${data.length} recommendations`);
  } catch (err) {
    console.log('Cache write error:', err.message);
  }
};

export default function useFundamentalSignals() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMarket, setCurrentMarket] = useState('US');
  const [currentFilter, setCurrentFilter] = useState('ALL');

  const fetchRecommendations = async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = getCachedData();
        if (cached) {
          setRecommendations(cached);
          setError(null);
          setLoading(false);
          return;
        }
      }

      // Fetch fresh data using API client
      const data = await api.fetchRecommendations();
      setRecommendations(data || []);
      setError(null);

      // Cache the fresh data
      if (data && data.length > 0) {
        setCachedData(data);
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const filteredRecommendations = recommendations.filter(stock => {
    const marketMatch = stock.market === currentMarket;
    const filterMatch = currentFilter === 'ALL' || stock.recommendation === currentFilter;
    return marketMatch && filterMatch;
  });

  const recommendationCounts = {
    STRONG_BUY: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'STRONG_BUY').length,
    BUY_FUNDAMENTALS: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'BUY_FUNDAMENTALS').length,
    BUY_MOMENTUM: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'BUY_MOMENTUM').length,
    BUY_LOW_CONFIDENCE: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'BUY_LOW_CONFIDENCE').length,
    HOLD_QUALITY: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'HOLD_QUALITY').length,
    HOLD_MOMENTUM: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'HOLD_MOMENTUM').length,
    HOLD: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'HOLD').length,
    AVOID: recommendations.filter(r => r.market === currentMarket && r.recommendation === 'AVOID').length,
  };

  // Market counts - always show total stocks per market regardless of filter
  const marketCounts = {
    US: recommendations.filter(r => r.market === 'US').length,
    IN: recommendations.filter(r => r.market === 'IN').length,
    TEST: recommendations.filter(r => r.market === 'TEST').length,
  };

  const fetchStockDetails = async (ticker, market = 'US') => {
    try {
      // Fetch fundamentals via API client
      let fundamentals = null;
      try {
        fundamentals = await api.fetchStockData('fundamentals', { ticker });
      } catch (e) {
        console.log('No fundamentals data');
      }

      // Fetch wavetrend signals via API client (per-ticker endpoint)
      let wavetrendData = { strategies: [], ticker_quality_score: null };
      try {
        const signalsData = await api.fetchWaveTrendChart(ticker, market);
        wavetrendData = {
          strategies: signalsData?.strategies || [],
          ticker_quality_score: signalsData?.ticker_quality_score || null
        };
      } catch (e) {
        console.log('No wavetrend data');
      }

      // Fetch peer group data via API client
      let peerGroupData = null;
      try {
        const peerData = await api.fetchStockData('peers', { ticker });
        if (peerData) {
          peerGroupData = {
            method: peerData.peer_selection_method,
            reasoning: peerData.reasoning,
            peers: peerData.peers
          };
        }
      } catch (peerErr) {
        console.log('No peer group data available');
      }

      return { fundamentals, wavetrendData, peerGroup: peerGroupData };
    } catch (err) {
      console.error('Error fetching stock details:', err);
      return { fundamentals: null, wavetrendData: { strategies: [], ticker_quality_score: null }, peerGroup: null };
    }
  };

  return {
    recommendations: filteredRecommendations,
    loading,
    error,
    currentMarket,
    setCurrentMarket,
    currentFilter,
    setCurrentFilter,
    recommendationCounts,
    marketCounts,
    refresh: () => fetchRecommendations(true),
    fetchStockDetails
  };
}
